import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { useStore } from 'zustand';
import { useAuthStore } from '@/stores/authStore';
import {
  readWorkingView, workingViewAuthScope, workingViewStore, writeWorkingView,
  type WorkingViewAddress, type WorkingViewDefinition,
} from '@/stores/workingViewStore';

export interface ScrollAnchor { id: string | null; offset: number; left: number }
const definition: WorkingViewDefinition<ScrollAnchor> = {
  id: 'pane-scroll-v1',
  parse(input) {
    if (!input || typeof input !== 'object') return null;
    const v = input as Partial<ScrollAnchor>;
    if (!(v.id === null || (typeof v.id === 'string' && v.id.length <= 512))
      || !Number.isFinite(v.offset) || !Number.isFinite(v.left)) return null;
    return { id: v.id, offset: Math.max(-100000, Math.min(100000, v.offset!)), left: Math.max(0, Math.min(100000, v.left!)) };
  },
};

/** Adapter returns stable item identities, not cached DOM nodes or row indices. */
export interface WorkingScrollOptions {
  pane: string;
  instance: string;
  context: string;
  ready: boolean;
  occlusion?: number;
  enabled?: boolean;
}
function anchorId(el: HTMLElement): string {
  const section = el.parentElement?.closest<HTMLElement>('section[data-view-anchor]');
  return JSON.stringify([section?.dataset.viewAnchor ?? null, el.dataset.viewAnchor]);
}
export function captureScrollAnchor(root: HTMLElement, occlusion = 0): ScrollAnchor {
  const top = root.getBoundingClientRect().top + occlusion;
  const items = Array.from(root.querySelectorAll<HTMLElement>('[data-view-anchor]'));
  const item = items.find((el) => !el.querySelector('[data-view-anchor]') && el.getBoundingClientRect().bottom > top)
    ?? items.find((el) => el.getBoundingClientRect().bottom > top);
  return { id: item ? anchorId(item) : null,
    offset: item ? item.getBoundingClientRect().top - top : 0, left: root.scrollLeft };
}
export function restoreScrollAnchor(root: HTMLElement, anchor: ScrollAnchor, occlusion = 0): boolean {
  const item = Array.from(root.querySelectorAll<HTMLElement>('[data-view-anchor]'))
    .find((el) => anchorId(el) === anchor.id);
  if (anchor.id !== null && !item) return false;
  root.scrollTop = item
    ? Math.max(0, root.scrollTop + item.getBoundingClientRect().top
      - root.getBoundingClientRect().top - occlusion - anchor.offset) : 0;
  root.scrollLeft = anchor.left;
  return true;
}

/** One restore per mounted context; no feed-driven recentering or retained pages. */
export function useWorkingScroll(options: WorkingScrollOptions) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [nodeVersion, setNodeVersion] = useState(0);
  const ref = useCallback((element: HTMLDivElement | null) => {
    rootRef.current = element;
    setNodeVersion((v) => v + 1);
  }, []);
  const location = useLocation();
  const navigation = useNavigationType();
  const scope = useAuthStore((s) => s.status === 'unauthenticated' ? null : workingViewAuthScope(s.user));
  const epoch = useStore(workingViewStore, (s) => s.epoch);
  const { pane, instance, context, ready, occlusion = 0, enabled = true } = options;
  const readyRef = useRef(ready);
  const attemptRef = useRef<(() => void) | null>(null);
  const previousRef = useRef<{ visit: string; context: string; root: HTMLElement } | null>(null);
  useLayoutEffect(() => { readyRef.current = ready; attemptRef.current?.(); }, [ready]);
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root || !scope || !enabled) return;
    const address: WorkingViewAddress = { scope, epoch, view: definition.id,
      instance: JSON.stringify([pane, instance, context]),
      entry: JSON.stringify([location.key, location.pathname, location.search]) };
    const visit = JSON.stringify([scope, epoch, pane, instance, address.entry]);
    const prior = previousRef.current;
    const inPlaceContextChange = prior?.visit === visit && prior.root === root && prior.context !== context;
    previousRef.current = { visit, context, root };
    // A filter/density edit is not a return journey. Keep the reader where
    // they are now; the new context gets its own anchor for a later return.
    const anchor = inPlaceContextChange ? null : readWorkingView(definition, address, navigation !== 'POP');
    if (!anchor && !inPlaceContextChange) { root.scrollTop = 0; root.scrollLeft = 0; }
    let restoring = anchor !== null;
    let frame = 0;
    let disposed = false;
    const capture = () => {
      if (!disposed && !restoring && root.clientHeight > 0) {
        writeWorkingView(definition, address, captureScrollAnchor(root, occlusion));
      }
    };
    // Scroll events can be frequent; coalesce geometry reads only. Market and
    // execution updates are not scheduled, delayed or consumed by this hook.
    const onScroll = () => {
      // While waiting we have not moved the pane ourselves. Any intervening
      // scroll wins, including dragging its scrollbar or assistive navigation.
      if (restoring) stopRestore();
      if (!frame) frame = requestAnimationFrame(() => { frame = 0; capture(); });
    };
    const observer = new MutationObserver(() => attempt());
    const stopRestore = () => { restoring = false; observer.disconnect(); };
    const attempt = () => {
      if (!restoring || disposed || !readyRef.current || root.clientHeight <= 0) return;
      if (anchor && restoreScrollAnchor(root, anchor, occlusion)) stopRestore();
    };
    attemptRef.current = attempt;
    const interrupt = () => { if (restoring) stopRestore(); };
    const onKey = (e: KeyboardEvent) => {
      if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(e.key)) interrupt();
    };
    root.addEventListener('wheel', interrupt, { passive: true });
    root.addEventListener('touchstart', interrupt, { passive: true });
    root.addEventListener('pointerdown', interrupt, { passive: true });
    root.addEventListener('keydown', onKey);
    root.addEventListener('scroll', onScroll, { passive: true });
    if (restoring) observer.observe(root, { childList: true, subtree: true });
    attempt();
    // Missing/withdrawn item: leave this pane at its normal start. Never alter
    // filters to recover an anchor, and never move it after the reader takes over.
    const timeout = setTimeout(() => { if (restoring) { root.scrollTop = 0; stopRestore(); } }, 1500);
    return () => {
      capture();
      disposed = true;
      attemptRef.current = null;
      observer.disconnect();
      clearTimeout(timeout);
      cancelAnimationFrame(frame);
      root.removeEventListener('wheel', interrupt);
      root.removeEventListener('touchstart', interrupt);
      root.removeEventListener('pointerdown', interrupt);
      root.removeEventListener('keydown', onKey);
      root.removeEventListener('scroll', onScroll);
    };
  // `ready`/feed changes never re-arm a completed or interrupted restoration.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeVersion, scope, epoch, pane, instance, context, location.key, location.pathname, location.search, enabled, occlusion]);
  return ref;
}
